import { useEffect, useState } from "react"

import { App, Col, Divider, Flex, Modal, Row, Space, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"

import ButtonComponent from "../../Components/Button"
import InputComponent from "../../Components/Input"
import {
  RecoveryCodesBlock,
  ScanCodeBlock,
  TwoFactorSuccessModal
} from "../../Components/TwoFactor"
import { useExternalDemoMode } from "../../hooks/useExternalDemoMode"
import { useQuery } from "../../Utils/utils"
import { authErrorReset } from "../LoginPage/redux/auth.actions"
import { authActions } from "../LoginPage/redux/auth.slice"
import {
  commitAdminSecurity2FA,
  confirmAdminSecurity2FA,
  generateAdminSecurity2FA
} from "../LoginPage/redux/auth.thunks"
import ChangePasswordForm from "../PersonalSettings/MyProfile/ChangePasswordForm"
import CheckEmail from "../PersonalSettings/MyProfile/CheckEmail"
import CreateNewPassword from "../PersonalSettings/MyProfile/CreateNewPassword"
import { userActions } from "../User/redux/user.slice"
import {
  changePassword,
  createNewPassword,
  sendCodePassword,
  setPassword
} from "../User/redux/user.thunks"

import "./styles.less"

const { Title, Text } = Typography
const AUTH_CODE_ERROR = "Two-factor code verification failed. Please try again."

const STEP_QR = "qr"
const STEP_RECOVERY = "recovery"

const usePageQuery = (search) => {
  const query = useQuery(search)

  return {
    action: query.get("action"),
    code: query.get("code")
  }
}

const SecurityPage = () => {
  const { t } = useTranslation()
  const { notification } = App.useApp()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { search } = useLocation()
  const { action, code } = usePageQuery(search)
  const externalDemoMode = useExternalDemoMode()
  const userInfo = useSelector((state) => state.auth.userInfo)
  const isAdmin = useSelector((state) => state.auth.isAdmin)
  const accessToken = useSelector((state) => state.auth.accessToken)
  const authError = useSelector((state) => state.auth.error)
  const authFetching = useSelector((state) => state.auth.fetching)
  const twoFactorQrCode = useSelector((state) => state.auth.twoFactorQrCode)
  const twoFactorSecret = useSelector((state) => state.auth.twoFactorSecret)
  const twoFactorSetupToken = useSelector((state) => state.auth.twoFactorSetupToken)
  const twoFactorRecoveryCodes = useSelector((state) => state.auth.twoFactorRecoveryCodes)
  const twoFactorCommitToken = useSelector((state) => state.auth.twoFactorCommitToken)
  const saveFetching = useSelector((state) => state.user.saveFetching)
  const saveResult = useSelector((state) => state.user.saveResult)
  const userError = useSelector((state) => state.user.errors)
  const isMobile = useSelector((state) => state.nav.isMobile)

  const [visible, setVisible] = useState(false)
  const [modal, setModal] = useState("")
  const [field, setField] = useState("")
  const [modalWidth, setModalWidth] = useState(460)

  const [step, setStep] = useState(STEP_QR)
  const [verificationError, setVerificationError] = useState("")
  const [isWaitingForCommit, setIsWaitingForCommit] = useState(false)
  const [isCommitInFlight, setIsCommitInFlight] = useState(false)

  const { email, password_created } = userInfo

  const notificationConfig = {
    closeIcon: <Text>{t("Dismiss")}</Text>
  }

  useEffect(() => {
    document.title = "Security settings - Chargeback"
  }, [])

  useEffect(() => {
    if (code && action === "forgot_password") {
      handleChangeModal("createNewPassword")
      setVisible(true)
    }
  }, [action, code])

  useEffect(() => {
    if (saveResult && !saveResult?.message && field === "password") {
      notification.success({
        message: t("Password changed"),
        description: t("Your password was changed successfully."),
        ...notificationConfig,
        style: { minWidth: "420px" }
      })
      setField("")
      setVisible(false)
      dispatch(userActions.userSaveResultReset())
    }
  }, [dispatch, field, notification, saveResult, t])

  useEffect(() => {
    if (saveResult?.setPassword) {
      notification.success({
        message: t("A password has been successfully set!"),
        ...notificationConfig,
        style: { minWidth: "420px" }
      })
      setVisible(false)
      dispatch(userActions.userSaveResultReset())
    }
  }, [dispatch, notification, saveResult, t])

  useEffect(() => {
    if (!isAdmin || !accessToken) return

    setStep(STEP_QR)
    setVerificationError("")
    dispatch(authActions.clear2FASetupState())
    dispatch(generateAdminSecurity2FA())
  }, [accessToken, dispatch, isAdmin])

  useEffect(() => {
    if (!isAdmin) return

    if (twoFactorRecoveryCodes?.length) {
      setStep(STEP_RECOVERY)
      return
    }

    if (isWaitingForCommit || (visible && modal === "twoFactorSuccess")) return

    setStep(STEP_QR)
  }, [isAdmin, isWaitingForCommit, modal, twoFactorRecoveryCodes, visible])

  useEffect(() => {
    if (!authError || !twoFactorSetupToken) return

    setVerificationError(AUTH_CODE_ERROR)
    dispatch(authErrorReset())
  }, [authError, dispatch, twoFactorSetupToken])

  useEffect(() => {
    if (!isWaitingForCommit || !authFetching) return

    setIsCommitInFlight(true)
  }, [authFetching, isWaitingForCommit])

  useEffect(() => {
    if (!isWaitingForCommit || !isCommitInFlight || authFetching || authError) {
      return
    }

    setIsWaitingForCommit(false)
    setIsCommitInFlight(false)
    setVisible(true)
    handleChangeModal("twoFactorSuccess")
  }, [authError, authFetching, isCommitInFlight, isWaitingForCommit])

  useEffect(() => {
    if (!isWaitingForCommit || !isCommitInFlight || !authError) return

    setIsWaitingForCommit(false)
    setIsCommitInFlight(false)
  }, [authError, isCommitInFlight, isWaitingForCommit])

  const handleSubmit = (current, password) => {
    if (modal === "changePassword") {
      dispatch(changePassword({ current, password }))
      setField("password")
    }
  }

  const handleCreatePasswordSubmit = (password) => {
    if (password_created) {
      dispatch(createNewPassword({ password, confirm_code: code }))
      setField("password")
    } else {
      dispatch(setPassword({ password }))
    }
  }

  const handleClose = () => {
    setVisible(false)

    if (modal === "twoFactorSuccess") {
      dispatch(authActions.clear2FASetupState())
      setStep(STEP_QR)
      setVerificationError("")
      if (accessToken) {
        dispatch(generateAdminSecurity2FA())
      }
    }

    if (modal === "createNewPassword" && code) {
      navigate("/security", { replace: true })
    }
  }

  const handleChangeModal = (name) => {
    dispatch(userActions.userErrorsReset())
    dispatch(userActions.userSaveResultReset())
    setModal(name)
    // setModal("twoFactorSuccess");

    if (name === "forgotPassword") {
      setModalWidth(442)
    } else {
      setModalWidth(460)
    }
  }

  const handleSendCodePassword = () => {
    dispatch(sendCodePassword(email))
  }

  const handleOpenTwoFactorSuccessModal = () => {
    if (!twoFactorCommitToken) return

    setIsWaitingForCommit(true)
    dispatch(
      commitAdminSecurity2FA({
        commit_token: twoFactorCommitToken
      })
    )
  }

  const handleConfirmAdminSecurity2FA = ({ otp, rememberDevice }) => {
    setVerificationError("")

    if (!twoFactorSetupToken) return

    dispatch(
      confirmAdminSecurity2FA({
        setup_token: twoFactorSetupToken,
        otp,
        trust_device: rememberDevice
      })
    )
  }

  const handleCancelRecoveryCodes = () => {
    setStep(STEP_QR)
    setVerificationError("")
    dispatch(authActions.clear2FASetupState())
    dispatch(generateAdminSecurity2FA())
  }

  const changePasswordBlock = (
    <div>
      <Text type="secondary" style={{ display: "inline-block", marginBottom: 16 }}>
        {t("Your new password must be different from previously used passwords.")}
      </Text>

      <ChangePasswordForm
        onSubmit={handleSubmit}
        onClose={handleClose}
        userError={userError}
        saveFetching={saveFetching}
        userErrorsReset={() => dispatch(userActions.userErrorsReset())}
        saveResult={saveResult}
        userSaveResultReset={() => dispatch(userActions.userSaveResultReset())}
        onChangeModal={handleChangeModal}
        sendCodePassword={handleSendCodePassword}
        setField={setField}
      />
    </div>
  )

  const createNewPasswordBlock = (
    <div>
      <Text type="secondary" style={{ display: "inline-block", marginBottom: 16 }}>
        {password_created
          ? t("Your new password must be different from previously used passwords.")
          : null}
      </Text>

      <CreateNewPassword
        password_created={password_created}
        onSubmit={handleCreatePasswordSubmit}
        onClose={handleClose}
        userError={userError}
        userErrorsReset={() => dispatch(userActions.userErrorsReset())}
        userSaveResultReset={() => dispatch(userActions.userSaveResultReset())}
        saveResult={saveResult}
      />
    </div>
  )

  const forgotPasswordBlock = (
    <div>
      <CheckEmail data={{ email }} onResend={handleSendCodePassword} email={email} />
    </div>
  )

  const modalBlocks = {
    changePassword: {
      component: changePasswordBlock,
      title: t("Change Password")
    },
    forgotPassword: {
      component: forgotPasswordBlock,
      title: null
    },
    createNewPassword: {
      component: createNewPasswordBlock,
      title: password_created ? t("Create new password") : t("Create password")
    }
  }

  const currentModal = modalBlocks[modal]

  return (
    <div className="users-page">
      <Title level={3} style={{ textAlign: "left", marginBottom: 16 }}>
        {t("Security settings")}
      </Title>

      <Divider />

      <Row>
        <Col flex={"1 1 300px"}>
          <Space size={8} direction={"vertical"} className="security-password-block">
            <Text strong>{t("Password")}</Text>
            <Text type="secondary">
              {t(
                "We strongly advise you to use a strong password containing at least 8 characters, with a mix of numbers and letters."
              )}
            </Text>
          </Space>
        </Col>
        <Col flex={"0 1 446px"}>
          <Flex gap={12} align={"center"} style={{ height: "100%" }}>
            <InputComponent
              placeholder={password_created ? "******************" : t("Not Set")}
              disabled
            />
            <ButtonComponent
              size="large"
              onClick={() => {
                setVisible(true)
                handleChangeModal(password_created ? "changePassword" : "createNewPassword")
              }}
              disabled={externalDemoMode}
            >
              {password_created ? t("Edit") : t("Add")}
            </ButtonComponent>
          </Flex>
        </Col>
      </Row>

      <Divider />
      <Row>
        <Col flex={"1 1 300px"}>
          <Space size={8} direction={"vertical"} className="security-password-block">
            <Text strong>{t("Two-factor authentication")}</Text>
            <Text type="secondary">
              {t(
                "Authenticator apps (like Google Authenticator) generate one-time codes used to verify your sign-in."
              )}
            </Text>
          </Space>
        </Col>
        <Col flex={"0 1 460px"}>
          {step === STEP_QR && (
            <ScanCodeBlock
              t={t}
              qrCodeSrc={twoFactorQrCode}
              setupKey={twoFactorSecret}
              otpError={verificationError}
              isSubmitting={authFetching}
              onSubmit={handleConfirmAdminSecurity2FA}
            />
          )}
          {step === STEP_RECOVERY && (
            <RecoveryCodesBlock
              t={t}
              recoveryCodes={twoFactorRecoveryCodes}
              onSuccess={handleOpenTwoFactorSuccessModal}
              onCancel={handleCancelRecoveryCodes}
              isSubmitting={authFetching || isWaitingForCommit || isCommitInFlight}
            />
          )}
        </Col>
      </Row>

      <Modal
        {...(isMobile ? { transitionName: "" } : null)}
        open={visible && modal !== "twoFactorSuccess"}
        centered={!isMobile}
        width={currentModal?.width || modalWidth}
        title={currentModal?.title}
        footer={null}
        closeIcon={currentModal?.closeIcon}
        closable="true"
        onCancel={handleClose}
        destroyOnClose
        rootClassName={currentModal?.rootClassName}
        classNames={{
          content: cls({
            "clear-bg": true
          })
        }}
      >
        {currentModal?.component}
      </Modal>

      <TwoFactorSuccessModal
        open={modal === "twoFactorSuccess" && visible}
        onClose={handleClose}
        t={t}
      />
    </div>
  )
}

export default SecurityPage
